import React from "react";
import styled from "styled-components";
import { InnerLayout } from "../styles/Layouts";
import SectionTitle from "./SectionTitle";
import SmallTitle from "./SmallTitle";
import { Briefcase, GraduationCap } from "lucide-react";
import ResumeItem from "./ResumeItem";
import { FadeIn } from "./AnimatedComponents";

const Resume: React.FC = () => {
  return (
    <ResumeStyled>
      <FadeIn direction="up">
        <SectionTitle title={"Resume"} span={"resume"} />
      </FadeIn>
      <InnerLayout>
        <FadeIn direction="up" delay={0.1}>
          <div className="section-block">
            <SmallTitle icon={<Briefcase />} title={"Experience"} />
          </div>
        </FadeIn>
        <div className="timeline">
          <ResumeItem
            year={"March 2025 - Present"}
            title={"Software Engineer"}
            subTitle={"Microsoft"}
            slug={"microsoft-swe"}
            textArr={[
              `Spearheaded the end-to-end re-architecture of the Azure Stack Migrate portal, rewriting 88% of the codebase — overhauling the data translation layer, redesigning the appliance registration system, and migrating the entire repo to modern React patterns. Drove test coverage from 0% to 86% with 500+ unit and integration tests, establishing the team's first GA-quality testing infrastructure.`,
              `Identified critical GA gaps and independently pitched, prototyped and shipped two new platform extensions — Azure Migrate CLI (Python) and Azure Migrate Terraform (HCL/Go) — coordinating across cross-functional teams in 4 countries. Authored design documents, RFCs and user guides that defined technical direction for both extensions. Became the assigned owner and maintainer, achieving feature parity with Portal and PowerShell.`,
              `Drove measurable performance and reliability gains across the portal: 88.3% reduction in LCP, 90.6% reduction in CLS, 78% fewer data-fetching queries, 87.6% fewer ESLint errors, and 71.8% less code duplication. Triaged and resolved a P0 customer-blocking security vulnerability that threatened the GA timeline.`,
              `Architected a Unified Appliance Management System that consolidated four separate management portals into a single gateway, and engineered an Azure Arc Resource Reuse Integration that eliminated redundant infrastructure provisioning — reducing deployment costs and streamlining the migration workflow for enterprise customers.`,
              `Shipped production features across multiple Azure surfaces — Private Endpoint Enablement for Azure Migrate, 3 Storage Replica features for Windows Admin Center, GA readiness improvements (pagination, search, race condition fixes), and led the SDDC extension migration to a new monorepo architecture.`,
              `Designed and deployed Azure Local Sizer, a full-stack cloud cost estimation tool with dynamic visualizations and a backend optimization API, serving enterprise customers evaluating Azure infrastructure investments.`,
            ]}
          />
          <ResumeItem
            year={"May 2024 - Present"}
            title={"Deep Learning Software Researcher"}
            subTitle={"University of Waterloo"}
            slug={"waterloo-ml-researcher"}
            textArr={[
              `Lead author of peer-reviewed research accepted at IEEE ICMLA, VISAPP 2026 and ICECET 2026 — proposed a novel adversarial defence strategy for 3D deep learning models that improved classification accuracy by up to 40% under attack scenarios.`,
              `Architected a voxel grid 3D convolutional neural network (CNN) pipeline trained on adversarially attacked datasets, combining custom data augmentation, model hardening, and evaluation frameworks using Python and PyTorch3D.`,
              `Designed and executed comprehensive experimentation across multiple adversarial attack vectors — evaluating model robustness, tuning hyperparameters, and benchmarking defence performance against state-of-the-art approaches.`,
            ]}
          />
          <ResumeItem
            year={"September 2024 - December 2024"}
            title={"R&D Engineer"}
            subTitle={"Synopsys (via Ansys)"}
            slug={"synopsys-rnd"}
            textArr={[
              "Owned the modernization of the core UI component library powering Synopsys' 3D physics simulation software — joined through Ansys following its acquisition by Synopsys, working on infrastructure used by engineering teams worldwide.",
              "Redesigned and rebuilt core components using C# and WPF to align with modern design patterns, improving architecture consistency, maintainability and developer velocity across the simulation product suite.",
              "Engineered a file upload component with integrated 3D rendering capabilities, enabling users to import CAD models and visualize them directly within the simulation scene — streamlining the model ingestion workflow.",
              "Drove component standardization across the platform, reducing UI inconsistencies and accelerating feature development for downstream simulation tools.",
            ]}
          />
          <ResumeItem
            year={"January 2024 - April 2024"}
            title={"Software Developer"}
            subTitle={"D2L"}
            slug={"d2l-swe"}
            textArr={[
              "Designed and built reusable Table components for D2L's central design system — the component library behind Brightspace, used by 20+ universities across Canada.",
              "Tables supported sorting, filtering, and pagination out of the box, standardizing how course data, grades, and enrollment information are presented across the platform.",
              "Built with JavaScript and Lit, following WCAG accessibility standards for consistency across D2L's product suite.",
            ]}
          />
          <ResumeItem
            year={"September 2023 - December 2023"}
            title={"Software Engineer"}
            subTitle={"Microsoft"}
            slug={"microsoft-intern-2"}
            textArr={[
              "Shipped cloud migration and storage management features across the Azure portal and Windows Admin Center on the Azure Edge Devices team — second rotation, building on prior domain knowledge.",
            ]}
          />
          <ResumeItem
            year={"May 2023 - August 2023"}
            title={"Software Engineer"}
            subTitle={"Autodesk"}
            slug={"autodesk-swe"}
            textArr={[
              "Built an extension application for Dynamo Studio — a node-based visual coding language for CAD and construction model development within applications like Revit and Grasshopper — using C#, C++, JavaScript, WebGL and DirectX.",
              "Developed a 3D Large Model Viewer node extension to view, edit and load 3D models from the web within the desktop application and export them locally into Dynamo's environment.",
              "Extended the Dynamo 3D compiler Abstract Syntax Tree with dynamic model-generation from WebGL specifications to DirectX, enabling seamless conversion between web-based 3D formats and the native desktop rendering engine.",
              "Integrated Autodesk Construction Cloud Platform's authentication services into Dynamo using the Autodesk ID SDK, enabling seamless loading and saving of cloud projects from within the desktop environment.",
            ]}
          />
          <ResumeItem
            year={"January 2023 - April 2023"}
            title={"Full Stack Engineer"}
            subTitle={"Klue Labs"}
            slug={"klue-fse"}
            textArr={[
              "Built a full-stack integration app connecting the Klue competitive intelligence platform with Outreach.io using TypeScript, React, Redux Saga, Python, Pulumi, and nginx.",
              "Built a dashboard for running market analysis queries inside Outreach.io and visualizing results inline.",
              "Wrote the REST API backend to sync client data between Outreach and Klue's platform services.",
              "Deployed as Dockerized containers on Kubernetes via Pulumi, with CI/CD through GitHub Actions.",
            ]}
          />
          <ResumeItem
            year={"September 2022 - December 2022"}
            title={"Frontend Engineer"}
            subTitle={"Faire"}
            slug={"faire-fe"}
            textArr={[
              "Worked on the Brand Order section of Faire's wholesale marketplace, shipping features to the main service and internal tools using TypeScript, React, Jest, and MobX.",
              "Built an Order Triage panel for sellers to manage customer lists by purchase urgency, recency, and frequency.",
              "Added trace event and statistical analysis features to PETE, an internal event tracking tool, improving log coverage for debugging the main platform.",
              "Wrote unit and integration tests across PETE and Order Triage, reaching 85.7% and 90.1% coverage respectively.",
            ]}
          />
          <ResumeItem
            year={"May 2022 - August 2022"}
            title={"Software Engineer"}
            subTitle={"Microsoft"}
            slug={"microsoft-intern-1"}
            textArr={[
              "Built frontend testing infrastructure for the Azure Edge Devices team's portal applications. Patterns from this work were later adopted team-wide.",
            ]}
          />
          <ResumeItem
            year={"January 2022 - April 2022"}
            title={"Web Applications Developer"}
            subTitle={"BlackBerry"}
            slug={"blackberry-web"}
            textArr={[
              "Extended BlackBerry's CMS UI library to more efficiently create dynamic webpages by building new components and optimizing existing code using JavaScript, Adobe HTL, Adobe AEM and SCSS.",
              "Created a frontend component to load, display and edit legal patent documents directly from the CMS system onto live webpages.",
              "Authored and updated over 60 webpages across all BlackBerry domains — from localizing blog sites to updating the main homepage with new product announcements.",
              "Wrote unit and integration test suites to ensure reliability and code quality across frontend components.",
            ]}
          />
          <ResumeItem
            year={"September 2021 - December 2021"}
            title={"Software Developer"}
            subTitle={"Mely.ai"}
            slug={"melyai-swe"}
            textArr={[
              "Built backend services and REST APIs for Mely.ai's document processing platform — automation pipelines for classifying and extracting data from logistics documents.",
            ]}
          />
          <ResumeItem
            year={"May 2021 - August 2021"}
            title={"Software Developer"}
            subTitle={"BlackBerry"}
            slug={"blackberry-enterprise"}
            textArr={[
              "Developed AppInsight, an internal full-stack web tool to scan APK/IPA files (Android and iOS binaries) for security vulnerabilities, increasing the efficiency of the data analytics team using JavaScript, React, Redux Saga and Node.js.",
              "Built a backend API enabling users to upload/download report files and folders, run security scans and directly navigate project files from the browser.",
              "Created a responsive frontend client to display scan progress, support file and folder navigation, and manage upload/download operations from the cloud.",
              "Engineered automatic data fetching and background operation handling to ensure the app loads project data immediately and completes all operations asynchronously.",
            ]}
          />
          <ResumeItem
            year={"July 2021 - April 2022"}
            title={"Developer Student Clubs Lead"}
            subTitle={"Google Developers"}
            slug={"google-dsc-lead"}
            textArr={[
              "Founded and led a Google Developer Student Club chapter — managed 4 development teams (16 engineers) building sustainable technology solutions for the Google Solutions Challenge while organizing 10+ technical events and workshops.",
            ]}
          />
          <ResumeItem
            year={"October 2020 - January 2022"}
            title={"Computer Simulation Research Assistant"}
            subTitle={"Wilfrid Laurier University"}
            slug={"laurier-sim-ra"}
            textArr={[
              "Designed and developed 15+ simulation programs for kinematic physics experiments for classroom use in virtual physics labs using JavaScript and HTML5 Canvas.",
              "Created thermal expansion and conductivity, moment of inertia, pipe resonance and many other simulation applications to virtually conduct experiments with multiple materials and conditions.",
              "Prepared reports of research procedures, data gathered, code implementation and design documents for all simulations.",
              "Awarded a research scholarship in 2021 for developing simulation programs adopted for physics course use.",
            ]}
          />
          <ResumeItem
            year={"September 2020 - January 2022"}
            title={"Application Development Lead"}
            subTitle={"Laurier Computing Society"}
            slug={"lcs-app-dev-lead"}
            textArr={[
              "Led 20 developers building a resume generation and calendar management platform — designed the Node.js REST API for authentication and data persistence.",
            ]}
          />
          <ResumeItem
            year={"July 2020 - May 2021"}
            title={"Full Stack Developer"}
            subTitle={"Laurier Robotics"}
            slug={"laurier-robotics-dev"}
            textArr={[
              "Built and maintained the Laurier Robotics website with React and Firebase — serverless email contact system, dynamic events section, and technical documentation.",
            ]}
          />
        </div>

        <FadeIn direction="up">
          <div className="section-block education-section">
            <SmallTitle icon={<GraduationCap />} title={"Education"} />
          </div>
        </FadeIn>
        <div className="timeline">
          <ResumeItem
            year={""}
            title={"Masters of Data Science and Artificial Intelligence"}
            subTitle={"University of Waterloo"}
            textArr={[]}
          />
          <ResumeItem
            year={""}
            title={"Masters of Business Administration"}
            subTitle={"Windsor University"}
            textArr={[]}
          />
          <ResumeItem
            year={""}
            title={"Bachelor of Computer Science"}
            subTitle={"Wilfrid Laurier University"}
            textArr={[]}
          />
          <ResumeItem
            year={""}
            title={"Bachelor of Business Administration"}
            subTitle={"Wilfrid Laurier University"}
            textArr={[]}
          />
        </div>
      </InnerLayout>
    </ResumeStyled>
  );
};

const ResumeStyled = styled.section`
  .section-block {
    margin-bottom: 2rem;
  }

  .education-section {
    margin-top: 3rem;
  }

  .timeline {
    padding-left: 0.5rem;
  }
`;

export default Resume;
